import React, { useState } from 'react';
import {
  Card,
  Logo,
  MainBody,
  Pitch,
  InfoTitle,
  InfoValue,
  TopRow,
  LogoColumn,
  TopInfoColumn,
  InfoCol,
  InfoColFullWidth,
  Header,
  HeaderIconButton,
  HeaderText,
  SectionDivider,
  SectionLabel,
  SectionGrid,
  FocaisLink,
} from './styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core';
import { defaultStartupLogo } from './behavior';
import { getCredLocalStorage } from 'sharedKernel/auth/credLocalStorage';

interface BaseProps {
  logo?: string;
  pitch?: string;
  customPrimaryColor?: string;
  defaultCollapsed?: boolean;
}

interface StartupVariantProps extends BaseProps {
  variant?: 'startup';
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  CNPJ?: string;
  website?: string;
  numberEmployees?: string;
  address?: string;
  activity?: string;
  businessModel?: string;
  target?: string;
  technologies?: string;
  developmentStage?: string;
  annualTurnover?: string;
  investmentCaptured?: string;
  registrationDate?: string;
}

interface PublicAdminVariantProps extends BaseProps {
  variant: 'public-administration';
  publicAdministrationType?: string;
  cnpj?: string;
  estado?: string;
  cidade?: string;
  portePopulacional?: string;
  orcamentoAnual?: string;
  numeroServidores?: string;
  registrationDate?: string;
  representante?: string;
  email?: string;
  contato?: string;
  cpf?: string;
  cargo?: string;
  linkedin?: string;
  onVerPontosFocais?: () => void;
  dirigenteMaximo?: string;
  mandatoAte?: string;
  possuiAreaFormalInovacao?: string;
  areaFormalInovacao?: string;
  anoCriacao?: string;
  qtdePessoasEquipe?: string;
  temContratoGestaoICT?: string;
  realizouProcedimentosMarcoLegal?: string;
  verticaisImpacto?: string;
  objetivoEntrarPrograma?: string;
  existeOrcamentoCTI?: string;
  valorEstimadoCTI?: string;
  activity?: string;
}

export type DetailedStartupCardProps = StartupVariantProps | PublicAdminVariantProps;

export const DetailedStartupCard = (props: DetailedStartupCardProps) => {
  const {
    logo,
    pitch,
    customPrimaryColor = '#0066B3',
    defaultCollapsed = false,
    variant = 'startup',
  } = props;

  const Companytype = getCredLocalStorage()?.company?.type?.type;
  const [collapsed, setCollapsed] = useState(!!defaultCollapsed);

  const chevronRight: [IconPrefix, IconName] = ['fas', 'chevron-right'];
  const chevronDown: [IconPrefix, IconName] = ['fas', 'chevron-down'];

  const isPublicAdmin = variant === 'public-administration';
  const pa = isPublicAdmin ? (props as PublicAdminVariantProps) : null;
  const st = !isPublicAdmin ? (props as StartupVariantProps) : null;

  return (
    <Card $collapsed={collapsed}>
      <MainBody $collapsed={collapsed}>
        <Header $collapsed={collapsed} onClick={() => setCollapsed(prev => !prev)}>
          <HeaderIconButton aria-label={collapsed ? 'Expandir' : 'Recolher'} type="button">
            <FontAwesomeIcon icon={collapsed ? chevronRight : chevronDown} width={14} height={14} />
          </HeaderIconButton>
          <HeaderText>{isPublicAdmin ? 'Sobre o órgão público' : 'Sobre a startup'}</HeaderText>
        </Header>

        {!collapsed && (
          <>
            <TopRow>
              <LogoColumn>
                <Logo src={logo ?? defaultStartupLogo} alt="Logo" />
              </LogoColumn>
              <TopInfoColumn>
                <Pitch>{pitch ?? '-'}</Pitch>
              </TopInfoColumn>
            </TopRow>

            {!isPublicAdmin && st != null && (
              <>
                <SectionDivider />
                <SectionLabel>Informações gerais</SectionLabel>
                <SectionGrid>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>Representante</InfoTitle>
                    <InfoValue>{st.contactName ?? '-'}</InfoValue>
                  </InfoCol>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>Contato</InfoTitle>
                    <InfoValue>{st.contactPhone ?? '-'}</InfoValue>
                  </InfoCol>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>E-mail</InfoTitle>
                    <InfoValue>{st.contactEmail ?? '-'}</InfoValue>
                  </InfoCol>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>CNPJ</InfoTitle>
                    <InfoValue>{st.CNPJ ?? '-'}</InfoValue>
                  </InfoCol>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>Site</InfoTitle>
                    <InfoValue>{st.website ?? '-'}</InfoValue>
                  </InfoCol>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>Número de funcionários</InfoTitle>
                    <InfoValue>{st.numberEmployees ?? '-'}</InfoValue>
                  </InfoCol>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>Endereço</InfoTitle>
                    <InfoValue>{st.address ?? '-'}</InfoValue>
                  </InfoCol>
                </SectionGrid>

                <SectionDivider />
                <SectionLabel>Sobre o negócio</SectionLabel>
                <SectionGrid>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>Vertical/Setor</InfoTitle>
                    <InfoValue>{st.activity ?? '-'}</InfoValue>
                  </InfoCol>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>Modelo de negócio</InfoTitle>
                    <InfoValue>{st.businessModel ?? '-'}</InfoValue>
                  </InfoCol>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>Público alvo</InfoTitle>
                    <InfoValue>{st.target ?? '-'}</InfoValue>
                  </InfoCol>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>Tecnologias</InfoTitle>
                    <InfoValue>{st.technologies ?? '-'}</InfoValue>
                  </InfoCol>
                </SectionGrid>

                <SectionDivider />
                <SectionLabel>Sobre o desenvolvimento</SectionLabel>
                <SectionGrid>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>Estagio de desenvolvimento</InfoTitle>
                    <InfoValue>{st.developmentStage ?? '-'}</InfoValue>
                  </InfoCol>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>Faturamento anual</InfoTitle>
                    <InfoValue>{st.annualTurnover ?? '-'}</InfoValue>
                  </InfoCol>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>Investimento capturado</InfoTitle>
                    <InfoValue>{st.investmentCaptured ?? '-'}</InfoValue>
                  </InfoCol>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>Data de cadastro</InfoTitle>
                    <InfoValue>{st.registrationDate ?? '-'}</InfoValue>
                  </InfoCol>
                </SectionGrid>
              </>
            )}

            {isPublicAdmin && pa != null && (
              <>
                <SectionDivider />
                <SectionLabel>Identificação institucional</SectionLabel>
                <SectionGrid>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>Tipo de órgão</InfoTitle>
                    <InfoValue>{pa.publicAdministrationType ?? '-'}</InfoValue>
                  </InfoCol>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>CNPJ</InfoTitle>
                    <InfoValue>{pa.cnpj ?? '-'}</InfoValue>
                  </InfoCol>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>Estado</InfoTitle>
                    <InfoValue>{pa.estado ?? '-'}</InfoValue>
                  </InfoCol>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>Cidade</InfoTitle>
                    <InfoValue>{pa.cidade ?? '-'}</InfoValue>
                  </InfoCol>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>Porte populacional</InfoTitle>
                    <InfoValue>{pa.portePopulacional ?? '-'}</InfoValue>
                  </InfoCol>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>Orçamento anual estimado</InfoTitle>
                    <InfoValue>{pa.orcamentoAnual ?? '-'}</InfoValue>
                  </InfoCol>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>Número de servidores</InfoTitle>
                    <InfoValue>{pa.numeroServidores ?? '-'}</InfoValue>
                  </InfoCol>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>Data de cadastro</InfoTitle>
                    <InfoValue>{pa.registrationDate ?? '-'}</InfoValue>
                  </InfoCol>
                </SectionGrid>

                <SectionDivider />
                <SectionLabel>Dados do representante</SectionLabel>
                <SectionGrid>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>Representante</InfoTitle>
                    <InfoValue>{pa.representante ?? '-'}</InfoValue>
                  </InfoCol>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>E-mail</InfoTitle>
                    {Companytype === 'Resolver' ? <InfoValue>*******@****.com</InfoValue> : <InfoValue>{pa.email ?? '-'}</InfoValue>}
                  </InfoCol>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>Contato</InfoTitle>
                    {Companytype === 'Resolver' ? <InfoValue>(**) *****-****</InfoValue> : <InfoValue>{pa.contato ?? '-'}</InfoValue>}
                  </InfoCol>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>CPF</InfoTitle>
                    <InfoValue>{pa.cpf ?? '-'}</InfoValue>
                  </InfoCol>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>Cargo</InfoTitle>
                    <InfoValue>{pa.cargo ?? '-'}</InfoValue>
                  </InfoCol>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>Linkedin</InfoTitle>
                    <InfoValue>{pa.linkedin ?? '-'}</InfoValue>
                  </InfoCol>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>Pontos focais</InfoTitle>
                    {pa.onVerPontosFocais != null ? (
                      <FocaisLink type="button" onClick={pa.onVerPontosFocais}>
                        Ver outros pontos focais
                      </FocaisLink>
                    ) : (
                      <InfoValue>-</InfoValue>
                    )}
                  </InfoCol>
                </SectionGrid>

                <SectionDivider />
                <SectionLabel>Liderança e governança</SectionLabel>
                <SectionGrid>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>Dirigente máximo</InfoTitle>
                    <InfoValue>{pa.dirigenteMaximo ?? '-'}</InfoValue>
                  </InfoCol>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>Mandato até</InfoTitle>
                    <InfoValue>{pa.mandatoAte ?? '-'}</InfoValue>
                  </InfoCol>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>Possui área formal de inovação?</InfoTitle>
                    <InfoValue>{pa.possuiAreaFormalInovacao ?? '-'}</InfoValue>
                  </InfoCol>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>Área formal de inovação</InfoTitle>
                    <InfoValue>{pa.areaFormalInovacao ?? '-'}</InfoValue>
                  </InfoCol>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>Ano de criação</InfoTitle>
                    <InfoValue>{pa.anoCriacao ?? '-'}</InfoValue>
                  </InfoCol>
                  <InfoCol>
                    <InfoTitle $primaryColor={customPrimaryColor}>Quantidade de pessoas na equipe</InfoTitle>
                    <InfoValue>{pa.qtdePessoasEquipe ?? '-'}</InfoValue>
                  </InfoCol>
                  <InfoColFullWidth>
                    <InfoTitle $primaryColor={customPrimaryColor}>
                      Tem contrato de gestão com alguma ICT (Instituição de Ciência e Tecnologia), Parque Tecnológico, Hub, etc?
                    </InfoTitle>
                    <InfoValue>{pa.temContratoGestaoICT ?? '-'}</InfoValue>
                  </InfoColFullWidth>
                  <InfoColFullWidth>
                    <InfoTitle $primaryColor={customPrimaryColor}>
                      Já realizou procedimentos do marco legal para contratação inovadora?
                    </InfoTitle>
                    <InfoValue>{pa.realizouProcedimentosMarcoLegal ?? '-'}</InfoValue>
                  </InfoColFullWidth>
                </SectionGrid>

                <SectionDivider />
                <SectionLabel>Intenção com TEIA</SectionLabel>
                <SectionGrid>
                  <InfoColFullWidth>
                    <InfoTitle $primaryColor={customPrimaryColor}>Verticais de Impacto Prioritários</InfoTitle>
                    <InfoValue>{pa.verticaisImpacto ?? '-'}</InfoValue>
                  </InfoColFullWidth>
                  <InfoColFullWidth>
                    <InfoTitle $primaryColor={customPrimaryColor}>
                      Objetivo ao entrar no programa de residentes do Espaço TEIA:
                    </InfoTitle>
                    <InfoValue>{pa.objetivoEntrarPrograma ?? '-'}</InfoValue>
                  </InfoColFullWidth>
                  <InfoColFullWidth>
                    <InfoTitle $primaryColor={customPrimaryColor}>
                      Existe orçamento para CT&amp;I (Ciência, Tecnologia e Inovação)?
                    </InfoTitle>
                    <InfoValue>{pa.existeOrcamentoCTI ?? '-'}</InfoValue>
                  </InfoColFullWidth>
                  <InfoColFullWidth>
                    <InfoTitle $primaryColor={customPrimaryColor}>
                      Valor estimado anual disponível para CT&amp;I (Ciência, Tecnologia e Inovação):
                    </InfoTitle>
                    <InfoValue>{pa.valorEstimadoCTI ?? '-'}</InfoValue>
                  </InfoColFullWidth>
                </SectionGrid>
              </>
            )}
          </>
        )}
      </MainBody>
    </Card>
  );
};
